/** Tiny DOM helpers — the UI is built once and updated by text/class/style writes (no framework, no re-render churn). */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls = '',
  text?: string,
  parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

/** Set textContent only when it changed (avoids needless layout). */
export function setText(e: HTMLElement, text: string): void {
  if (e.textContent !== text) e.textContent = text;
}

export function toggle(e: HTMLElement, cls: string, on: boolean): void {
  if (e.classList.contains(cls) !== on) e.classList.toggle(cls, on);
}

export function html<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, inner: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const e = el(tag, cls, undefined, parent);
  e.innerHTML = inner;
  return e;
}

/**
 * A scroll cue for a box that scrolls: `cls` is on it while there is more below the fold and off once the
 * reader reaches the end (the CSS draws a torn-edge fade at the foot from it). Returns `update`, to call
 * after the box's content is rebuilt; scrolling and resizing are watched here.
 */
export function scrollCue(box: HTMLElement, cls = 'more'): () => void {
  const update = (): void => toggle(box, cls, box.scrollHeight - box.clientHeight - box.scrollTop > 4);
  box.addEventListener('scroll', update, { passive: true });
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(box);
  return () => {
    update();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(update);
  };
}

/** Escape text before interpolating into innerHTML. */
export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
